import { type InfiniteData } from "@tanstack/react-query";

import { AddCampaignJobBoardRequest } from "@/types/request/AddCampaignJobBoardRequest";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { PagedResponse } from "@/types/response/PagedResponse";

type CreateCampaignIntegrationResponse = {
  campaignId: string;
  clientIntegrationModuleId: string;
  clientId2: string | null;
  campaignName: string | null;
  integrationDisplayName: string | null;
};

export function updateInfiniteQueryCache(
  old: InfiniteData<PagedResponse<CampaignJobBoardType>> | undefined,
  response: CreateCampaignIntegrationResponse,
  variables: AddCampaignJobBoardRequest,
  clientId?: string
): InfiniteData<PagedResponse<CampaignJobBoardType>> {
  const newItem: CampaignJobBoardType = {
    campaignId: response.campaignId,
    clientId2: response.clientId2 ?? clientId ?? "",
    clientIntegrationModuleId: response.clientIntegrationModuleId,
    currentIncludeMargin: false,
    currentMonthBudget: variables.currentMonthBudget ?? variables.monthlyBudget ?? null,
    endDate: variables.endDate ?? null,
    isActive: new Date(variables.startDate) <= new Date() &&
      (!variables.endDate || new Date(variables.endDate) > new Date()),
    isExpired: !!variables.endDate && new Date(variables.endDate) <= new Date(),
    isPaused: false,
    integrationDisplayName: response.integrationDisplayName ?? null,
    monthlyBudget: variables.monthlyBudget,
    monthlyRecords: [],
    pendingIncludeMargin: false,
    selectedMonth: {
      actualSpent: null,
      budget: variables.currentMonthBudget ?? variables.monthlyBudget ?? 0,
      monthlyBudget: null,
      campaignId: response.campaignId,
      campaignName: response.campaignName ?? "",
      clientId2: response.clientId2 ?? clientId ?? "",
      clientName: "",
      includeMargin: false,
      invoiceableBudget: variables.currentMonthBudget ?? variables.monthlyBudget ?? 0,
      isInvoiced: false,
      clientIntegrationModuleId: response.clientIntegrationModuleId,
      integrationDisplayName: response.integrationDisplayName ?? "",
      month: "",
      spent: null,
      client: {
        id2: response.clientId2 ?? clientId ?? "",
        name: "",
      },
    },
    spent: 0,
    startDate: variables.startDate,
  };

  if (!old) {
    return {
      pages: [
        {
          data: [newItem],
          page: 1,
          pageSize: 10,
          totalRecords: 1,
        },
      ],
      pageParams: [1],
    };
  }

  const firstPage = old.pages[0];
  const hasExisting = firstPage?.data.some((item) => item.clientIntegrationModuleId === response.clientIntegrationModuleId);
  const filtered = firstPage?.data.filter((item) => item.clientIntegrationModuleId !== response.clientIntegrationModuleId) ?? [];

  const newItemName = (newItem.integrationDisplayName ?? newItem.jobBoardName ?? "").toLowerCase();
  const insertIndex = filtered.findIndex(
    (item) => (item.integrationDisplayName ?? item.jobBoardName ?? "").toLowerCase().localeCompare(newItemName) > 0
  );
  const sorted = insertIndex === -1
    ? [...filtered, newItem]
    : [...filtered.slice(0, insertIndex), newItem, ...filtered.slice(insertIndex)];

  return {
    ...old,
    pages: [
      {
        ...firstPage,
        data: sorted,
        totalRecords: firstPage.totalRecords + (hasExisting ? 0 : 1),
      },
      ...old.pages.slice(1),
    ],
  };
}
